import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();

/**
 * EDI849 Response Status enum
 */
export const EDI849ResponseStatusSchema = z.enum([
  "accepted",
  "rejected",
  "partial",
  "unknown",
  "resubmitted",
  /**
   * The email carried NO accept/reject decision but did say something the team
   * must act on (a request for a correct part number, a flagged SPA overlap) —
   * captured as an `advisories` entry. Distinct from `unknown`, which means "we
   * could not read a decision out of this": an advisory reads as a supplier
   * question awaiting our reply, not as a broken parse.
   */
  "advisory",
]);
export type EDI849ResponseStatus = z.infer<typeof EDI849ResponseStatusSchema>;

/**
 * Pagination schema
 */
export const EDI849PaginationSchema = z.object({
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});

/**
 * Email summary for response
 */
const ResponseEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string().nullable().optional(),
  from: z.string().nullable().optional(),
  receivedAt: z.string(),
});

/**
 * Line result from 849 response (individual transaction)
 */
export const EDI849LineResultSchema = z.object({
  status: z.string(),
  matchFields: z.record(z.string(), z.unknown()),
  rawData: z.record(z.string(), z.unknown()).optional(),
  columnOrder: z.array(z.string()).optional(),
  rejectionCode: z.string().optional(),
  rejectionReason: z.string().optional(),
  gentlyRejectionCode: z.string().optional(),
  /**
   * Whether this 849 line was confidently tied back to an 844 we sent:
   * "matched" (exactly one sent line), "ambiguous" (multiple — not applied), or
   * "unmatched" (none / too few identity fields).
   * Absent on older responses parsed before this field existed.
   */
  matchStatus: z.enum(["matched", "ambiguous", "unmatched"]).optional(),
  /** The sd_e844d id this line tied to (when matched) */
  matchedSdE844dId: z.string().optional(),
});
export type EDI849LineResult = z.infer<typeof EDI849LineResultSchema>;

/**
 * Submitted 844 line data (original claim data from rexel_e844sd)
 */
const SubmittedLineDataSchema = z.object({
  sdE844dId: z.string().nullable().optional(),
  submissionId: z.string().nullable().optional(),
  invoiceNumber: z.string(),
  orderLineNumber: z.number(),
  batchControlNumber: z.string(),
  claimAmount: z.number(),
  buyerCost: z.number(),
  shippedQuantity: z.number(),
  customerName: z.string(),
  itemNumber: z.string(),
  supplierItemCode: z.string(),
  poNumber: z.string(),
  orderNumber: z.number(),
  branchName: z.string(),
  supplierContractNo: z.string(),
});

/**
 * Linked VDM summary (for 849 response cross-referencing via 844 submissions)
 */
const LinkedVDMSummarySchema = z.object({
  id: UUIDSchema,
  gentlyVdmNumber: z.number().int(),
  vdmNumber: z.number().int().nullable(),
  status: z.string(),
  grossAmount: z.string(),
});

/**
 * Linked 844 submission summary (for 849 response cross-referencing)
 */
const LinkedSubmissionSummarySchema = z.object({
  id: UUIDSchema,
  submissionNumber: z.number().int(),
  companyName: z.string().nullable(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  status: z.string(),
  deliveryMethod: z.string(),
  totalClaimedAmount: z.string(),
  sentAt: z.string().nullable(),
  vdm: LinkedVDMSummarySchema.nullable(),
  vdms: z.array(LinkedVDMSummarySchema).optional(),
});

/**
 * Non-decision context extracted from a supplier S&D email (e.g. a pre-denial
 * inquiry). Informational only — never affects line decisions or amounts.
 */
export const EDI849AdvisorySchema = z.object({
  summary: z.string(),
  requestedAction: z.string().nullable(),
  contact: z.string().nullable(),
  lines: z.array(
    z.object({
      supplierProductCode: z.string().nullable(),
      description: z.string().nullable(),
      issueCode: z.string().nullable(),
      issueText: z.string().nullable(),
      matchedSdE844dId: z.string().nullable(),
    }),
  ),
  source: z.literal("email"),
  extractedAt: z.string(),
});
export type EDI849Advisory = z.infer<typeof EDI849AdvisorySchema>;

/**
 * EDI849 Response schema
 */
export const EDI849ResponseSchema = z.object({
  id: UUIDSchema,
  inboundEmailId: UUIDSchema.nullable(),
  supplierId: z.string(),
  supplierName: z.string(),
  status: z.string(),
  acceptedCount: z.number().int().nonnegative(),
  rejectedCount: z.number().int().nonnegative(),
  partialCount: z.number().int().nonnegative(),
  lineResults: z.array(EDI849LineResultSchema),
  sourceType: z.string(),
  parentResponseId: UUIDSchema.nullable(),
  processedAt: z.string(),
  rawEdiContent: z.string().nullable().optional(),
  batchNumbers: z.array(z.string()).optional(),
  divisions: z.array(z.string()).optional(),
  /** Persisted division resolved from the EDI buyer segment (null if unresolved). */
  divisionCode: z.string().nullable().optional(),
  /** Per-status dollar amounts for this response (credited for accepted/partial, claimed for partial/rejected). */
  amounts: z
    .object({
      acceptedApproved: z.number(),
      partialApproved: z.number(),
      partialClaimed: z.number(),
      /**
       * Claimed $ on rejected lines. Suppliers usually echo $0 on rejected
       * lines in the 849, so when the 849 value is not meaningful this falls
       * back to the matched 844 submitted claim amount. {@link rejectedClaimedSource}
       * records where the figure came from.
       */
      rejectedClaimed: z.number(),
      /**
       * Where {@link rejectedClaimed} came from: `"response"` = the 849 echo,
       * `"submitted"` = the 844 we sent (849 echoed $0), `"mixed"` = both
       * across different rejected lines, `"none"` = no rejected amount.
       */
      rejectedClaimedSource: z.enum(["response", "submitted", "mixed", "none"]),
      /**
       * Total claimed $ across all lines (claimReceivedAmount, with the same
       * 844 submitted-claim fallback applied when the 849 echoed $0). This is
       * what the whole response is "worth".
       */
      totalClaimed: z.number(),
      /** Total credited $ across all lines (what the supplier actually approved: accepted + partial). */
      totalCredited: z.number(),
    })
    .optional(),
  email: ResponseEmailSchema.optional(),
  linkedSubmissions: z.array(LinkedSubmissionSummarySchema).optional(),
  /**
   * New 844 submissions that were created by resubmitting rejected/partial
   * lines of THIS 849 (clone model). Distinct from `linkedSubmissions`, which
   * are the original submissions this 849 responded to.
   */
  resubmittedSubmissions: z.array(LinkedSubmissionSummarySchema).optional(),
  submittedLines: z.array(SubmittedLineDataSchema).optional(),
  investigationStatus: z
    .enum(["pending", "running", "completed", "failed"])
    .nullable()
    .optional(),
  investigationResults: z
    .array(
      z.object({
        lineIndex: z.number().int().nonnegative(),
        rejectionCode: z.string().nullable(),
        gentlyRejectionCode: z.string().nullable(),
        strategySource: z.enum([
          "user_configured",
          "supplier_default",
          "generic_default",
          "none",
        ]),
        rootCause: z.string(),
        rootCauseCategory: z.enum([
          "missing_bpid",
          "wrong_bpid",
          "stale_bpid",
          "vendor_number_mismatch",
          "item_ineligible",
          "contract_ineligible",
          "unknown",
        ]),
        evidence: z.array(
          z.object({ source: z.string(), finding: z.string() }),
        ),
        suggestedFix: z.string().nullable(),
        suggestedFixType: z
          .enum(["add_bpid", "update_bpid", "resubmit", "escalate"])
          .nullable(),
        fixParameters: z.record(z.string(), z.unknown()).nullable(),
        confidence: z.number().min(0).max(1),
        isOurIssue: z.boolean(),
      }),
    )
    .nullable()
    .optional(),
  investigatedAt: z.string().nullable().optional(),
  investigationEnabled: z.boolean().optional(),
  /** Non-decision supplier context (detail view only — full payload). */
  advisories: z.array(EDI849AdvisorySchema).nullable().optional(),
  /** Lightweight flag for the list view (full advisories only on detail). */
  hasAdvisory: z.boolean().optional(),
  investigationFeedback: z
    .array(
      z.object({
        lineIndex: z.number(),
        decision: z.enum(["agree", "disagree"]),
        correctRootCause: z.string().nullable().optional(),
        correctFix: z.string().nullable().optional(),
        reason: z.string().nullable().optional(),
        feedbackAt: z.string(),
        feedbackByUserId: z.string(),
      }),
    )
    .nullable()
    .optional(),
});
export type EDI849Response = z.infer<typeof EDI849ResponseSchema>;

// ----------------------------------------
// List EDI849 Responses
// ----------------------------------------
export const ListEDI849ResponsesInputSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
  supplierIds: z.array(z.string()).optional(),
  statuses: z.array(EDI849ResponseStatusSchema).optional(),
  /** Source type filter — "email" (Excel attachment) or "edi" (X12 attachment). */
  sourceTypes: z.array(z.enum(["email", "edi"])).optional(),
  /** Processed date range — entries like "gte:YYYYMMDD" / "lte:YYYYMMDD" / "eq:YYYYMMDD". Matches the DateFilter component's output. */
  processedAt: z.array(z.string()).optional(),
  /** Browser timezone offset in minutes (from Date.getTimezoneOffset()). Positive = west of UTC. Used to anchor YYYYMMDD dates to the user's local day. */
  timezoneOffset: z.number().int().min(-840).max(720).optional(),
  /** Batch number filter — each entry is an exact batch ("12345") or an inclusive range ("12341-12349"). Entries are OR'd. Matches the BatchNumberFilter component's output. */
  batchNumber: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  /** Division codes to filter by (matches linked 844 submission division_code). */
  divisions: z.array(z.string()).optional(),
});
export type ListEDI849ResponsesInput = z.infer<
  typeof ListEDI849ResponsesInputSchema
>;

/**
 * A row in the 849 responses list — {@link EDI849ResponseSchema} minus the
 * heavy `lineResults` array, plus a `lineCount` total.
 *
 * `lineResults` averages ~73 kB per response (worst case seen: 5.3 MB), so a
 * 25-row page shipped ~1.7 MB of it — uncompressed, to users on ~0.25 Mbps —
 * and the list never rendered a single line. Everything the list needs is
 * already derived server-side from those lines: `batchNumbers`, `divisions`,
 * `amounts`, the per-status counts, and now `lineCount`. The full array is
 * still returned by the `get` (detail) endpoint.
 */
export const EDI849ResponseListItemSchema = EDI849ResponseSchema.omit({
  lineResults: true,
}).extend({
  /**
   * How many parsed entries are in this response's `lineResults`.
   *
   * NOT interchangeable with `acceptedCount + partialCount + rejectedCount`.
   * Those record the decision; this records how much line-level detail we
   * managed to parse, and the two legitimately diverge when a supplier sends a
   * header-level decision with no itemised lines — e.g. 5 Superior Essex email
   * responses on prod carry `rejectedCount: 1` with an empty `lineResults`. A
   * row showing 0 lines against a non-zero status count is that case, not a
   * bug: it means we recorded a decision we have no line breakdown for.
   */
  lineCount: z.number().int().nonnegative(),
});
export type EDI849ResponseListItem = z.infer<
  typeof EDI849ResponseListItemSchema
>;

export const EDI849ResponseTotalsSchema = z.object({
  totalAcceptedLines: z.number().int().nonnegative(),
  totalRejectedLines: z.number().int().nonnegative(),
  totalPartialLines: z.number().int().nonnegative(),
  totalApprovedAmount: z.number().nonnegative(),
});

export const ListEDI849ResponsesOutputSchema = z.object({
  responses: z.array(EDI849ResponseListItemSchema),
  pagination: EDI849PaginationSchema,
  totals: EDI849ResponseTotalsSchema,
});
export type ListEDI849ResponsesResponse = z.infer<
  typeof ListEDI849ResponsesOutputSchema
>;

export const listEDI849ResponsesContract = oc
  .input(ListEDI849ResponsesInputSchema)
  .output(ListEDI849ResponsesOutputSchema);

// ----------------------------------------
// Get EDI849 Response
// ----------------------------------------
export const GetEDI849ResponseInputSchema = z.object({
  id: UUIDSchema,
});
export type GetEDI849ResponseInput = z.infer<
  typeof GetEDI849ResponseInputSchema
>;

export const GetEDI849ResponseOutputSchema = EDI849ResponseSchema.nullable();
export type GetEDI849ResponseResponse = z.infer<
  typeof GetEDI849ResponseOutputSchema
>;

export const getEDI849ResponseContract = oc
  .input(GetEDI849ResponseInputSchema)
  .output(GetEDI849ResponseOutputSchema);

// ----------------------------------------
// Delete EDI849 Response (Admin only)
// ----------------------------------------
export const DeleteEDI849ResponseInputSchema = z.object({
  id: UUIDSchema,
});
export type DeleteEDI849ResponseInput = z.infer<
  typeof DeleteEDI849ResponseInputSchema
>;

export const DeleteEDI849ResponseOutputSchema = z.object({
  success: z.boolean(),
});
export type DeleteEDI849ResponseResponse = z.infer<
  typeof DeleteEDI849ResponseOutputSchema
>;

export const deleteEDI849ResponseContract = oc
  .input(DeleteEDI849ResponseInputSchema)
  .output(DeleteEDI849ResponseOutputSchema);

// ----------------------------------------
// Update Supplier on 849 Response (single)
// ----------------------------------------
export const UpdateEDI849ResponseSupplierInputSchema = z.object({
  id: UUIDSchema,
  supplierId: z.string().uuid(),
});
export type UpdateEDI849ResponseSupplierInput = z.infer<
  typeof UpdateEDI849ResponseSupplierInputSchema
>;

export const UpdateEDI849ResponseSupplierOutputSchema = z.object({
  success: z.boolean(),
  supplierId: z.string(),
  supplierName: z.string(),
});
export type UpdateEDI849ResponseSupplierOutput = z.infer<
  typeof UpdateEDI849ResponseSupplierOutputSchema
>;

export const updateEDI849ResponseSupplierContract = oc
  .input(UpdateEDI849ResponseSupplierInputSchema)
  .output(UpdateEDI849ResponseSupplierOutputSchema);

// ----------------------------------------
// Bulk Assign Supplier to unmatched 849 Responses
// ----------------------------------------
export const BulkAssignSupplierInputSchema = z.object({
  responseIds: z.array(UUIDSchema).min(1),
  supplierId: z.string().uuid(),
});
export type BulkAssignSupplierInput = z.infer<
  typeof BulkAssignSupplierInputSchema
>;

export const BulkAssignSupplierOutputSchema = z.object({
  success: z.boolean(),
  updatedCount: z.number().int().nonnegative(),
  supplierName: z.string(),
});
export type BulkAssignSupplierOutput = z.infer<
  typeof BulkAssignSupplierOutputSchema
>;

export const bulkAssignSupplierContract = oc
  .input(BulkAssignSupplierInputSchema)
  .output(BulkAssignSupplierOutputSchema);

// ----------------------------------------
// User Response Schemas
// ----------------------------------------

/**
 * User response status enum
 */
export const SD849UserResponseStatusSchema = z.enum([
  "in_progress",
  "ready_to_send",
  "sent",
]);
export type SD849UserResponseStatus = z.infer<
  typeof SD849UserResponseStatusSchema
>;

/**
 * Line decision by user
 */
export const LineDecisionSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  decision: z.enum(["agree", "disagree"]),
  reason: z.string().optional(),
  decidedAt: z.string(),
  decidedByUserId: z.string().uuid(),
});
export type LineDecision = z.infer<typeof LineDecisionSchema>;

/**
 * Proof attachment info
 */
export const ProofAttachmentSchema = z.object({
  id: UUIDSchema,
  fileName: z.string(),
  fileSize: z.number(),
  mimeType: z.string(),
  uploadedAt: z.string(),
});
export type ProofAttachment = z.infer<typeof ProofAttachmentSchema>;

/**
 * Full user response schema
 */
export const SD849UserResponseSchema = z.object({
  id: UUIDSchema,
  sd849ResponseId: UUIDSchema,
  status: SD849UserResponseStatusSchema,
  lineDecisions: z.array(LineDecisionSchema),
  emailBodyTemplate: z.string().nullable().optional(),
  responseDocumentId: UUIDSchema.nullable().optional(),
  proofAttachments: z.array(ProofAttachmentSchema),
  sentAt: z.string().nullable().optional(),
  sentByUserId: UUIDSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SD849UserResponse = z.infer<typeof SD849UserResponseSchema>;

// ----------------------------------------
// Get or Create User Response
// ----------------------------------------
export const GetOrCreateUserResponseInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});
export type GetOrCreateUserResponseInput = z.infer<
  typeof GetOrCreateUserResponseInputSchema
>;

export const GetOrCreateUserResponseOutputSchema = SD849UserResponseSchema;
export type GetOrCreateUserResponseOutput = z.infer<
  typeof GetOrCreateUserResponseOutputSchema
>;

export const getOrCreateUserResponseContract = oc
  .input(GetOrCreateUserResponseInputSchema)
  .output(GetOrCreateUserResponseOutputSchema);

// ----------------------------------------
// Save Line Decision
// ----------------------------------------
export const SaveLineDecisionInputSchema = z.object({
  userResponseId: UUIDSchema,
  lineIndex: z.number().int().nonnegative(),
  decision: z.enum(["agree", "disagree"]),
  reason: z.string().optional(),
});
export type SaveLineDecisionInput = z.infer<typeof SaveLineDecisionInputSchema>;

export const SaveLineDecisionOutputSchema = z.object({
  success: z.boolean(),
  userResponse: SD849UserResponseSchema,
});
export type SaveLineDecisionOutput = z.infer<
  typeof SaveLineDecisionOutputSchema
>;

export const saveLineDecisionContract = oc
  .input(SaveLineDecisionInputSchema)
  .output(SaveLineDecisionOutputSchema);

// ----------------------------------------
// Update Email Body
// ----------------------------------------
export const UpdateEmailBodyInputSchema = z.object({
  userResponseId: UUIDSchema,
  emailBodyTemplate: z.string(),
});
export type UpdateEmailBodyInput = z.infer<typeof UpdateEmailBodyInputSchema>;

export const UpdateEmailBodyOutputSchema = z.object({
  success: z.boolean(),
});
export type UpdateEmailBodyOutput = z.infer<typeof UpdateEmailBodyOutputSchema>;

export const updateEmailBodyContract = oc
  .input(UpdateEmailBodyInputSchema)
  .output(UpdateEmailBodyOutputSchema);

// ----------------------------------------
// Get Proof Upload URL
// ----------------------------------------
export const GetProofUploadUrlInputSchema = z.object({
  userResponseId: UUIDSchema,
  fileName: z.string(),
  mimeType: z.string(),
  fileSize: z.number().int().positive(),
});
export type GetProofUploadUrlInput = z.infer<
  typeof GetProofUploadUrlInputSchema
>;

export const GetProofUploadUrlOutputSchema = z.object({
  uploadUrl: z.string(),
  documentId: UUIDSchema,
});
export type GetProofUploadUrlOutput = z.infer<
  typeof GetProofUploadUrlOutputSchema
>;

export const getProofUploadUrlContract = oc
  .input(GetProofUploadUrlInputSchema)
  .output(GetProofUploadUrlOutputSchema);

// ----------------------------------------
// Confirm Proof Upload
// ----------------------------------------
export const ConfirmProofUploadInputSchema = z.object({
  userResponseId: UUIDSchema,
  documentId: UUIDSchema,
});
export type ConfirmProofUploadInput = z.infer<
  typeof ConfirmProofUploadInputSchema
>;

export const ConfirmProofUploadOutputSchema = z.object({
  success: z.boolean(),
  proofAttachments: z.array(ProofAttachmentSchema),
});
export type ConfirmProofUploadOutput = z.infer<
  typeof ConfirmProofUploadOutputSchema
>;

export const confirmProofUploadContract = oc
  .input(ConfirmProofUploadInputSchema)
  .output(ConfirmProofUploadOutputSchema);

// ----------------------------------------
// Remove Proof Attachment
// ----------------------------------------
export const RemoveProofAttachmentInputSchema = z.object({
  userResponseId: UUIDSchema,
  documentId: UUIDSchema,
});
export type RemoveProofAttachmentInput = z.infer<
  typeof RemoveProofAttachmentInputSchema
>;

export const RemoveProofAttachmentOutputSchema = z.object({
  success: z.boolean(),
  proofAttachments: z.array(ProofAttachmentSchema),
});
export type RemoveProofAttachmentOutput = z.infer<
  typeof RemoveProofAttachmentOutputSchema
>;

export const removeProofAttachmentContract = oc
  .input(RemoveProofAttachmentInputSchema)
  .output(RemoveProofAttachmentOutputSchema);

// ----------------------------------------
// Generate Response Excel
// ----------------------------------------
export const GenerateResponseExcelInputSchema = z.object({
  userResponseId: UUIDSchema,
});
export type GenerateResponseExcelInput = z.infer<
  typeof GenerateResponseExcelInputSchema
>;

export const GenerateResponseExcelOutputSchema = z.object({
  downloadUrl: z.string(),
  documentId: UUIDSchema,
});
export type GenerateResponseExcelOutput = z.infer<
  typeof GenerateResponseExcelOutputSchema
>;

export const generateResponseExcelContract = oc
  .input(GenerateResponseExcelInputSchema)
  .output(GenerateResponseExcelOutputSchema);

// ----------------------------------------
// Send Response to Supplier
// ----------------------------------------
export const SendResponseToSupplierInputSchema = z.object({
  userResponseId: UUIDSchema,
  emailBody: z.string(),
});
export type SendResponseToSupplierInput = z.infer<
  typeof SendResponseToSupplierInputSchema
>;

export const SendResponseToSupplierOutputSchema = z.object({
  success: z.boolean(),
  sentAt: z.string(),
});
export type SendResponseToSupplierOutput = z.infer<
  typeof SendResponseToSupplierOutputSchema
>;

export const sendResponseToSupplierContract = oc
  .input(SendResponseToSupplierInputSchema)
  .output(SendResponseToSupplierOutputSchema);

// ----------------------------------------
// Download 849 Responses Excel
// ----------------------------------------
export const Download849ResponsesExcelInputSchema = z.object({
  supplierIds: z.array(z.string()).optional(),
  statuses: z.array(EDI849ResponseStatusSchema).optional(),
  sourceTypes: z.array(z.enum(["email", "edi"])).optional(),
  processedAt: z.array(z.string()).optional(),
  timezoneOffset: z.number().int().min(-840).max(720).optional(),
  // Batch number filter — exact ("12345") or inclusive range ("12341-12349"); entries OR'd. See BatchNumberFilter.
  batchNumber: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  divisions: z.array(z.string()).optional(),
});
export type Download849ResponsesExcelInput = z.infer<
  typeof Download849ResponsesExcelInputSchema
>;

export const Download849ResponsesExcelOutputSchema = z.object({
  /** base64-encoded .xlsx bytes */
  excel: z.string(),
  filename: z.string(),
});
export type Download849ResponsesExcelOutput = z.infer<
  typeof Download849ResponsesExcelOutputSchema
>;

export const download849ResponsesExcelContract = oc
  .input(Download849ResponsesExcelInputSchema)
  .output(Download849ResponsesExcelOutputSchema)
  .route({ method: "POST" });

// ----------------------------------------
// Trigger Investigation
// ----------------------------------------
const TriggerInvestigationInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});

const TriggerInvestigationOutputSchema = z.object({
  success: z.boolean(),
  agentRunId: z.string().uuid().optional(),
  message: z.string(),
});

export const triggerInvestigationContract = oc
  .input(TriggerInvestigationInputSchema)
  .output(TriggerInvestigationOutputSchema)
  .route({ method: "POST" });

// ----------------------------------------
// Save Investigation Feedback
// ----------------------------------------
const SaveInvestigationFeedbackInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndex: z.number().int().min(0),
  decision: z.enum(["agree", "disagree"]),
  correctRootCause: z.string().optional(),
  correctFix: z.string().optional(),
  reason: z.string().optional(),
});

const SaveInvestigationFeedbackOutputSchema = z.object({
  success: z.boolean(),
});

export const saveInvestigationFeedbackContract = oc
  .input(SaveInvestigationFeedbackInputSchema)
  .output(SaveInvestigationFeedbackOutputSchema)
  .route({ method: "POST" });

// ----------------------------------------
// 849 Rejection Resubmission
// ----------------------------------------

/** A transaction parked for resubmission (rows of the "To Resubmit" table). */
export const ResubmissionCandidateSchema = z.object({
  sdE844dId: UUIDSchema,
  rexelE844sdId: UUIDSchema,
  originalSubmissionId: UUIDSchema.nullable(),
  invoiceNo: z.string(),
  orderLineNo: z.number(),
  tradingPartner: z.string().nullable(),
  division: z.string().nullable(),
  sellingLocation: z.string().nullable(),
  claimAmount: z.number(),
  unitPriceWholesale: z.number().nullable(),
  unitPriceQuoted: z.number().nullable(),
  quantity: z.number().nullable(),
  supplierContractNo: z.string().nullable(),
  rejectionCode: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  dataChanged: z.boolean(),
  /** Full sd_e844d row (snake_case, same shape as the dashboard list) — fed to
   *  TransactionEditModal so 844D fields are edited in the standard modal. */
  transaction: z.object({ id: UUIDSchema }).passthrough(),
});
export type ResubmissionCandidate = z.infer<typeof ResubmissionCandidateSchema>;

export const GetResubmissionCandidatesInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  /** The transactions the user has disputed (frontend state) — read-only.
   *  Optional with an empty-array default: this field was added later, so a
   *  stale frontend bundle (or any caller) that omits it must not hard-fail
   *  input validation with a 422. The handler already treats `[]` as "no
   *  disputed transactions yet" (returns enablement + resubmitted locks only). */
  transactionIds: z.array(UUIDSchema).optional().default([]),
});
export const GetResubmissionCandidatesOutputSchema = z.object({
  resubmissionEnabled: z.boolean(),
  candidates: z.array(ResubmissionCandidateSchema),
  /** `invoice|orderLine` keys of lines already resubmitted (locked) for this 849. */
  resubmittedKeys: z.array(z.string()),
});
export const getResubmissionCandidatesContract = oc
  .input(GetResubmissionCandidatesInputSchema)
  .output(GetResubmissionCandidatesOutputSchema);

export const CheckResubmissionDataInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
});
export const ResubmissionDataCheckSchema = z.object({
  id: UUIDSchema,
  unchanged: z.boolean(),
  changedFields: z.array(z.string()),
});
export const CheckResubmissionDataOutputSchema = z.object({
  checks: z.array(ResubmissionDataCheckSchema),
});
export const checkResubmissionDataContract = oc
  .input(CheckResubmissionDataInputSchema)
  .output(CheckResubmissionDataOutputSchema)
  .route({ method: "POST" });

const ResubmissionSubmissionSummarySchema = z.object({
  id: UUIDSchema,
  division: z.string(),
  batchNumber: z.string().nullable(),
  status: z.string(),
  transactionCount: z.number(),
  totalClaimedAmount: z.string(),
});

export const ResubmitRejectedInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  transactionIds: z.array(UUIDSchema).min(1),
  /** Per-transaction field overrides (snake_case → value), applied to the clone
   *  server-side. Frontend-staged edits — never written to the original. */
  edits: z.record(UUIDSchema, z.record(z.string(), z.unknown())).optional(),
  /** Required to proceed when any selected transaction has unchanged data. */
  confirmUnchanged: z.boolean().optional(),
});
export const ResubmitRejectedOutputSchema = z.object({
  /** True when the call was blocked by the unchanged-data guard. */
  blockedByUnchangedData: z.boolean(),
  unchangedIds: z.array(UUIDSchema),
  successful: z.array(UUIDSchema),
  failed: z.array(z.object({ id: UUIDSchema, reason: z.string() })),
  submissions: z.array(ResubmissionSubmissionSummarySchema),
  resubmittedFromSubmissionIds: z.array(UUIDSchema),
});
export const resubmitRejectedContract = oc
  .input(ResubmitRejectedInputSchema)
  .output(ResubmitRejectedOutputSchema)
  .route({ method: "POST" });

// ----------------------------------------
// 849 Line Decisions (agree = not disputing / disagree = disputing)
// ----------------------------------------

/** Our stance on a line the supplier didn't fully credit. */
export const SD849LineDecisionValueSchema = z.enum(["agree", "disagree"]);
export type SD849LineDecisionValue = z.infer<
  typeof SD849LineDecisionValueSchema
>;

/**
 * One recorded decision about a line the supplier rejected. The identity and
 * claim amount are SNAPSHOTTED when the user decides, so the downstream report
 * shows what was decided rather than what the row looks like later.
 */
export const SD849LineDecisionSchema = z.object({
  id: UUIDSchema,
  /** Index into the response's `lineResults`. */
  lineIndex: z.number().int().nonnegative(),
  decision: SD849LineDecisionValueSchema,
  /** The supplier decision responded to: "rejected" or "partial". */
  lineStatus: z.string(),
  sdE844dId: UUIDSchema.nullable(),
  invoiceNo: z.string().nullable(),
  orderLineNo: z.number().int().nullable(),
  batchControlNo: z.string().nullable(),
  /** What we claimed on the 844 — the dollars at stake on this line. */
  claimAmount: z.number().nullable(),
  note: z.string().nullable(),
  decidedAt: z.coerce.date(),
  decidedByUserId: UUIDSchema.nullable(),
  decidedByUserName: z.string().nullable(),
});
export type SD849LineDecision = z.infer<typeof SD849LineDecisionSchema>;

/**
 * The decision state for a response: whether decisions are recorded at all
 * (`decisionsEnabled` — EDI + a decision-enabled supplier, resolved server-side
 * so the frontend never hardcodes a supplier list) plus what has been decided.
 *
 * Deliberately its own small query rather than fields on `get`: deciding must
 * refresh this, and refetching `get` would re-ship the whole `lineResults`
 * payload (~73 kB typical, 5 MB worst case) to users on ~0.25 Mbps links.
 */
const LineDecisionStateSchema = z.object({
  decisionsEnabled: z.boolean(),
  decisions: z.array(SD849LineDecisionSchema),
});

export const ListLineDecisionsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});
export const listLineDecisionsContract = oc
  .input(ListLineDecisionsInputSchema)
  .output(LineDecisionStateSchema)
  .route({ method: "GET" });

export const RecordLineDecisionInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndexes: z.array(z.number().int().nonnegative()).min(1),
  decision: SD849LineDecisionValueSchema,
  note: z.string().trim().max(2000).optional(),
});
export const RecordLineDecisionOutputSchema = LineDecisionStateSchema.extend({
  /** How many rows this call wrote (re-recording the same decision is a no-op). */
  recorded: z.number().int().nonnegative(),
  /** Requested lines with nothing to decide (e.g. the supplier accepted them). */
  skipped: z.array(
    z.object({
      lineIndex: z.number().int().nonnegative(),
      reason: z.string(),
    }),
  ),
});
export const recordLineDecisionContract = oc
  .input(RecordLineDecisionInputSchema)
  .output(RecordLineDecisionOutputSchema)
  .route({ method: "POST" });

export const ClearLineDecisionsInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  lineIndexes: z.array(z.number().int().nonnegative()).min(1),
});
export const ClearLineDecisionsOutputSchema = LineDecisionStateSchema.extend({
  cleared: z.number().int().nonnegative(),
});
export const clearLineDecisionsContract = oc
  .input(ClearLineDecisionsInputSchema)
  .output(ClearLineDecisionsOutputSchema)
  .route({ method: "POST" });

// ----------------------------------------
// List Ship & Debit-enabled companies (for workflow config dropdowns)
// ----------------------------------------
export const SDCompanySchema = z.object({
  id: UUIDSchema,
  /** co_companies.name — the value the 849 identify step keys customerMappings by */
  name: z.string(),
  /** Preferred display name; falls back to name when null */
  shortName: z.string().nullable(),
});
export type SDCompany = z.infer<typeof SDCompanySchema>;

export const ListSDCompaniesOutputSchema = z.object({
  companies: z.array(SDCompanySchema),
});
export type ListSDCompaniesResponse = z.infer<
  typeof ListSDCompaniesOutputSchema
>;

export const listSDCompaniesContract = oc
  .input(z.object({}))
  .output(ListSDCompaniesOutputSchema);

// ----------------------------------------
// Activity timeline
// ----------------------------------------

/**
 * SD 849 Response Activity Type enum (matches the backend model
 * SD849ResponseActivityType).
 */
export const SD849ResponseActivityTypeSchema = z.enum([
  "created",
  "supplier_assigned",
  "lines_matched",
  "lines_rematched",
  "match_rerun",
  "resubmitted",
  "status_changed",
  "email_attached",
  "evolved",
  "expert_feedback",
  "line_agreed",
  "line_disagreed",
  "line_decision_cleared",
]);
export type SD849ResponseActivityTypeContract = z.infer<
  typeof SD849ResponseActivityTypeSchema
>;

const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

const ActivityInboundEmailSchema = z.object({
  id: UUIDSchema,
  subject: z.string().nullable().optional(),
  from: z.string().nullable().optional(),
});

export const SD849ResponseActivitySchema = z.object({
  id: UUIDSchema,
  sd849ResponseId: UUIDSchema,
  activityType: SD849ResponseActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  inboundEmail: ActivityInboundEmailSchema.nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});
export type SD849ResponseActivity = z.infer<typeof SD849ResponseActivitySchema>;

export const ListSD849ResponseActivitiesInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
  activityType: z.array(SD849ResponseActivityTypeSchema).optional(),
  limit: z.number().int().positive().max(200).optional(),
  offset: z.number().int().nonnegative().optional(),
  /** eventAt sort order (default "desc"). "asc" keeps the EARLIEST events when a page
   *  is capped — so the History timeline never loses its original `created` anchor. */
  order: z.enum(["asc", "desc"]).optional(),
});

export const ListSD849ResponseActivitiesResponseSchema = z.object({
  activities: z.array(SD849ResponseActivitySchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  }),
});
export type ListSD849ResponseActivitiesInput = z.infer<
  typeof ListSD849ResponseActivitiesInputSchema
>;
export type ListSD849ResponseActivitiesResponse = z.infer<
  typeof ListSD849ResponseActivitiesResponseSchema
>;

export const listSD849ResponseActivitiesContract = oc
  .input(ListSD849ResponseActivitiesInputSchema)
  .output(ListSD849ResponseActivitiesResponseSchema)
  .route({ method: "GET" });

// ----------------------------------------
// Re-run 849 → 844 line matching
// ----------------------------------------
const RerunMatchingInputSchema = z.object({
  sd849ResponseId: UUIDSchema,
});

const RerunMatchingOutputSchema = z.object({
  /** False when the re-run couldn't be attempted (see `message`), not an error. */
  ran: z.boolean(),
  message: z.string(),
  newlyLinked: z.number().int().optional(),
  linkedBefore: z.number().int().optional(),
  linkedAfter: z.number().int().optional(),
  totalResponseLines: z.number().int().optional(),
});

export const rerunMatchingContract = oc
  .input(RerunMatchingInputSchema)
  .output(RerunMatchingOutputSchema)
  .route({ method: "POST" });

// ----------------------------------------
// Contract router for edi849Responses
// ----------------------------------------
export const edi849ResponsesContract = {
  list: listEDI849ResponsesContract,
  get: getEDI849ResponseContract,
  delete: deleteEDI849ResponseContract,
  updateSupplier: updateEDI849ResponseSupplierContract,
  bulkAssignSupplier: bulkAssignSupplierContract,
  downloadExcel: download849ResponsesExcelContract,
  rerunMatching: rerunMatchingContract,
  triggerInvestigation: triggerInvestigationContract,
  saveInvestigationFeedback: saveInvestigationFeedbackContract,
  getResubmissionCandidates: getResubmissionCandidatesContract,
  checkResubmissionData: checkResubmissionDataContract,
  resubmitRejected: resubmitRejectedContract,
  listLineDecisions: listLineDecisionsContract,
  recordLineDecision: recordLineDecisionContract,
  clearLineDecisions: clearLineDecisionsContract,
  listSDCompanies: listSDCompaniesContract,
  listActivities: listSD849ResponseActivitiesContract,
};

// ----------------------------------------
// Contract router for edi849UserResponses
// ----------------------------------------
export const edi849UserResponsesContract = {
  getOrCreate: getOrCreateUserResponseContract,
  saveLineDecision: saveLineDecisionContract,
  updateEmailBody: updateEmailBodyContract,
  getProofUploadUrl: getProofUploadUrlContract,
  confirmProofUpload: confirmProofUploadContract,
  removeProofAttachment: removeProofAttachmentContract,
  generateResponseExcel: generateResponseExcelContract,
  sendToSupplier: sendResponseToSupplierContract,
};
